/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { getUserFacingError } from "../../utils/userFacingError";
import localforage from "localforage";
import Avatar from "@mui/material/Avatar";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CorporateFareTwoToneIcon from "@mui/icons-material/CorporateFareTwoTone";
import PeopleAltTwoToneIcon from "@mui/icons-material/PeopleAltTwoTone";
import VpnKeyTwoToneIcon from "@mui/icons-material/VpnKeyTwoTone";
import CreditCardTwoToneIcon from "@mui/icons-material/CreditCardTwoTone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInstagram,
  faFacebook,
  faXTwitter,
  faLinkedin,
  faYoutube,
  faTwitch,
  faTiktok,
  faBluesky,
} from "@fortawesome/free-brands-svg-icons";
import { Collapse, Menu, MenuItem, Pagination, SxProps } from "@mui/material";
import { enterpriseAPI, paymentAPI, RTA, sessionInfo } from "../..";
import { BannerFeedback } from "../../components/BannerFeedback";
import { buildOrgContactAvatarFields, createOrgAvatarUpdateHandler } from "./org.helpers";
import { Outlet } from "react-router-dom";
import { Title } from "../../components/Title";
import {
  faAdd,
  faChevronLeft,
  faClose,
  faCreditCard,
  faEdit,
  faFileInvoiceDollar,
  faKey,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { faEnvelope, faEye, faPaperPlane } from "@fortawesome/free-regular-svg-icons";
import { TextField } from "../../components/Field";
import { FloatContainer } from "../../components/FloatContainer";
import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { Topics } from "../../components/Topics";
import { Check, Close } from "@mui/icons-material";
import {
  checkPermission,
  RolePermissions,
  usePermissions,
} from "../../components/PermissionsContext";
import { createTurboUpload, getBandwidthMbps, validateField } from "../../utils/utils";
import { UPLOAD_BACKLOG_NOTICE } from "../../components/UploadToast";
import { InputTel } from "../../components/InputTel";
import Skeleton from "@mui/material/Skeleton";
import { useResolvedMedia } from "../../hooks/useResolvedMedia";
import { RoleCard } from "../../components/RoleCard";
import { RoleDrawer, DrawerMode } from "../../components/RoleDrawer";
import { TemplateChips } from "../../components/TemplateChips";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  formatBRL,
  InvoiceSplitPreviewModal,
} from "../../components/financial/InvoiceSplitPreviewModal";
import { WeeklyPixSummary, WeeklyStatementDialog } from "./org.transactions";
import { PixAutomaticoPanel, usePixAutomatico } from "../../components/PixAutomatico";
import { BalanceHistory, NegotiationBalancePanel } from "../../components/NegotiationBalance";
import { WeeklyChargeCard } from "../../components/financial/WeeklyChargeCard";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import {
  isPixAutomaticoTermsPublished,
  pixAutomaticoTermsVersion,
} from "../../services/pixAutomaticoTerms";
import {
  displayCardLabel,
  displayTransactionStatus,
  displayWeeklyPeriod,
  hasIdentifiedCard,
  NFE_AVAILABLE_ACTION_LABEL,
  parseWeeklyStatement,
  parseWeeklyPeriod,
  parseWeeklyCollection,
  resolveNFEIntent,
  splitStoredOrgPhone,
  transactionStatusChipClass,
} from "./org.utils";
import type { WeeklyPeriod, WeeklyCollection } from "./org.utils";
import {
  parseWeeklyBilling,
  parseWeeklyStatementPage,
  parseWeeklyStatementSummary,
} from "./org.weekly";
import type { WeeklyStatementPage, WeeklyStatementSummary } from "./org.weekly";
import type { SvgIconComponent } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import BadgeTwoToneIcon from "@mui/icons-material/BadgeTwoTone";
import { DEFAULT_ROLE_ICON, ROLE_TEMPLATES, RoleTemplate } from "../../utils/roleTemplates";
import "./org.css";

type RoleProps = {
  id: string;
  orgId: string;
  name: string;
  permissions: RolePermissions;
};

type CollaboratorPayload = {
  id: string;
  orgId: string;
  name: string;
  email: string;
  country?: string;
  state?: string;
  city?: string;
  zipCode?: string;
  numberStreet?: string;
  street?: string;
  neighborhood?: string;
  phone: number;
  avatar: string;
  roles: CollaboratorRolePayload[];
  isOwner: boolean;
};

type CollaboratorRolePayload = {
  id: string;
  name: string;
};

type OwnerPayload = {
  id: string;
  name: string;
  avatar: string;
};

type PendingInvitePayload = {
  id: string;
  email: string;
  status: string;
  createdAt: string;
  expiresAt: string;
};

type ListTransactions = {
  totalPages: number;
  items: Transaction[];
};

type Transaction = {
  invoiceId: string;
  balanceId: string;
  cardBrand: string;
  cardLabel: string;
  amount: number;
  grossAmountCents?: number;
  netAmountCents?: number;
  enterpriseResidualCents?: number;
  rewardsAmountCents?: number;
  contabileAmountCents?: number;
  enterpriseCashReceivedCents?: number;
  settlementStatus?: string;
  enterpriseSettlementStatus?: string;
  createdAt: string;
  invoiceStatus: string;
  invoiceType: string;
  weeklySummary?: WeeklyStatementSummary;
  weeklyBilling?: import("./org.weekly").WeeklyBilling;
  weeklyPeriod?: WeeklyPeriod;
  weeklyCollection?: WeeklyCollection;
  amountKnown?: boolean;
  paymentOrigin?: "weekly_pix";
  weeklyParentInvoiceId?: string;
};

type WeeklySelection = {
  invoiceId: string;
  summary?: WeeklyStatementSummary;
  period?: WeeklyPeriod;
  invoiceStatus?: string;
  collection?: WeeklyCollection;
  billing?: import("./org.weekly").WeeklyBilling;
};

type NFE = {
  internalId: string;
  documentNumber: number;
  numberSeries: number;
  // nfe_audit lifecycle status. The row is created when the transaction settles
  // and only completed when NFe.io answers, so a listed NFS-e can legitimately
  // carry no internalId and no document number yet (payments-api
  // controllers.go ListNFEsByBalanceID). Older payloads have no status field.
  status?: string;
};

// A document only exists — and only has a PDF at the provider — once the NFS-e
// was emitted. Every other status means the identifiers are still empty, so
// printing "#0-0" next to a "Visualizar" button that cannot resolve is a lie.
const NFE_PENDING_LABELS: { [status: string]: string } = {
  pending: "Em emissão",
  failed: "Falha na emissão",
  canceled: "Cancelada",
  "manual-operation": "Em operação manual",
};

const isNFEIssued = (nfe: NFE) => Boolean(nfe.internalId) && nfe.documentNumber > 0;

const nfePendingLabel = (nfe: NFE) => NFE_PENDING_LABELS[nfe.status || ""] || "Aguardando emissão";

function getBrandIcon(brand: string) {
  switch (brand) {
    case "mastercard":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/mastercard.png`}
          alt="Mastercard"
        />
      );
    case "visa":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/visa.png`}
          alt="Visa"
        />
      );
    case "american-express":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/amex.png`}
          alt="American Express"
        />
      );
    case "discover":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/discover.png`}
          alt="Discover"
        />
      );
    case "diners-club":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/diners.png`}
          alt="Diners Club"
        />
      );
    case "jcb":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/jcb.png`}
          alt="JCB"
        />
      );
    case "unionpay":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/unionpay.png`}
          alt="UnionPay"
        />
      );
    case "maestro":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/maestro.png`}
          alt="Maestro"
        />
      );
    case "elo":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/elo.png`}
          alt="Elo"
        />
      );
    case "hipercard":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/hipercard.png`}
          alt="Hipercard"
        />
      );
    case "hiper":
      return (
        <img
          className="brandCardIcon"
          src={`${window.location.origin}/assets/card-brands/hiper.png`}
          alt="Hiper"
        />
      );
    default:
      return <FontAwesomeIcon className="unknownBrandCardIcon" icon={faCreditCard} />;
  }
}

const NFEsModal = ({ balanceId, onClose }: { balanceId: string; onClose: () => void }) => {
  const [loading, setLoading] = React.useState(false);
  const [nfesError, setNFEsError] = React.useState("");
  // Kept apart from `nfesError`: that one REPLACES the modal body, which would
  // hide the very list the user is trying to download from. A failed PDF must
  // be reported next to the list, not instead of it.
  const [pdfError, setPDFError] = React.useState("");
  const [nfes, setNFEs] = React.useState<NFE[]>([]);

  React.useEffect(() => {
    if (!balanceId) return;
    api.getNFEs();
  }, [balanceId]);

  if (!balanceId) return <></>;

  class api {
    static async getNFEs() {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      // A missing session used to bail out with no message and the modal stuck
      // on its empty state — indistinguishable from "this transaction has no
      // notes". Say which one it is.
      if (!JWT) {
        setNFEsError("Sessão expirada. Entre novamente para ver as notas fiscais.");
        return false;
      }

      const headers: { [key: string]: string | undefined } = {
        "X-JWT": JWT,
      };

      setLoading(true);
      try {
        const result = await paymentAPI.get(`/transaction/${balanceId}/nfes`, {
          headers: headers,
          params: { balanceId },
        });
        setNFEs(result.data);
      } catch (error) {
        console.error("Error fetching NFEs:", error);
        setNFEsError(
          getUserFacingError(error, {
            fallback: "Não foi possível carregar as notas fiscais. Tente novamente em instantes.",
          }),
        );
      } finally {
        setLoading(false);
      }
    }

    static async viewNFEPDF(nfeId: string) {
      setPDFError("");

      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (!JWT) {
        setPDFError("Sessão expirada. Entre novamente para abrir o PDF da nota fiscal.");
        return false;
      }

      const headers: { [key: string]: string | undefined } = {
        "X-JWT": JWT,
      };

      try {
        const result = await paymentAPI.get(`/nfse/${nfeId}/pdf`, {
          responseType: "blob",
          headers: headers,
        });
        const blob = new Blob([result.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        const opened = window.open(url, "_blank");
        if (!opened) {
          // Pop-up blocked: the fetch worked but nothing appeared on screen,
          // which reads exactly like a dead button unless we say otherwise.
          setPDFError(
            "O PDF foi carregado mas o navegador bloqueou a nova aba. Permita pop-ups para este site.",
          );
        }

        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (error) {
        console.error("Error fetching NFE PDF:", error);
        setPDFError(
          getUserFacingError(error, {
            fallback: "Não foi possível abrir o PDF desta nota fiscal. Tente novamente.",
          }),
        );
      }
    }
  }

  return (
    <FloatContainer close={onClose}>
      <div id="nfeModalContainer">
        {loading ? (
          <>
            <Skeleton variant="rectangular" width="300px" height={40} />
            <Skeleton variant="rectangular" width="300px" height={40} />
            <Skeleton variant="rectangular" width="300px" height={40} />
          </>
        ) : nfesError ? (
          <div className="errorMessageNFEContainer" role="alert">
            <FontAwesomeIcon className="errorMessageNFEIcon" icon={faClose} />
            <span className="errorMessageNFEMessage">{nfesError}</span>
          </div>
        ) : nfes.length === 0 ? (
          <div className="noNFEMessageContainer">
            <FontAwesomeIcon className="noNFEMessageIcon" icon={faFileInvoiceDollar} />
            <span className="noNFEMessage">Não há notas fiscais disponíveis</span>
          </div>
        ) : (
          <>
            {pdfError && (
              <div className="errorMessageNFEContainer" role="alert" data-testid="nfe-pdf-error">
                <FontAwesomeIcon className="errorMessageNFEIcon" icon={faClose} />
                <span className="errorMessageNFEMessage">{pdfError}</span>
              </div>
            )}
            {nfes.map((nfe, index) => (
              // Not keyed on internalId: a note still waiting on the provider
              // has none, so several of them would collide on the empty string.
              <div key={`${nfe.internalId || "pending"}-${index}`} className="nfeItemContainer">
                <FontAwesomeIcon className="nfeItemIcon" icon={faFileInvoiceDollar} />
                {isNFEIssued(nfe) ? (
                  <>
                    <span className="nfeItemDocument">
                      #{nfe.documentNumber}-{nfe.numberSeries}
                    </span>
                    <Button
                      icon={<FontAwesomeIcon icon={faEye} />}
                      iconSlot="end"
                      callback={() => api.viewNFEPDF(nfe.internalId)}
                    >
                      Visualizar
                    </Button>
                  </>
                ) : (
                  <span className="nfeItemDocument nfeItemPending">{nfePendingLabel(nfe)}</span>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </FloatContainer>
  );
};

const avatarSX: SxProps = {
  width: "100%",
  height: "100%",
  backgroundColor: "var(--brand-a6)",
  objectFit: "cover",
  zIndex: 0,
};

const menuSX: SxProps = {
  "& .MuiPaper-root": {
    backgroundColor: "var(--brand-a5)",
    color: "var(--brand-12)",
  },
};

export function BasicInfo() {
  const [sendInProgress, setSendInProgress] = React.useState<boolean>(false);
  const [changeBasicInfo, setChangeBasicInfo] = React.useState(false);
  const [openBasicFeedback, setBasicFeedback] = React.useState(false);
  const [typeBasicFeedback, setTypeBasicFeedback] = React.useState<
    "error" | "success" | "warning" | "info"
  >("error");
  const [basicMessage, setBasicMessage] = React.useState("");
  const [showAvatar, setShowAvatar] = React.useState("");
  const [avatarLoading, setAvatarLoading] = React.useState(false);
  // Safety net: the RTA avatar-update handler and the org record can carry a
  // raw compressor manifest (.json) instead of an image URL. Resolve it before
  // binding to <Avatar src> so the logo never renders as a broken image while
  // the backend emit fix propagates.
  const resolvedShowAvatar = useResolvedMedia(showAvatar);
  const [organization, setOrganization] = React.useState({
    name: "",
    avatar: "",
    cnpj: "",
    duns: "",
    email: "",
    phoneArea: "",
    phoneCountry: "",
    phoneNumber: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
    numberStreet: "",
    street: "",
    neighborhood: "",
    website: "",
    instagram: "",
    facebook: "",
    linkedin: "",
    twitter: "",
    youtube: "",
    twitch: "",
    tiktok: "",
    bluesky: "",
  });

  // Controlled form state (edited values, separate from server-loaded organization)
  const [formFields, setFormFields] = React.useState({
    cnpj: "",
    duns: "",
    email: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
    numberStreet: "",
    street: "",
    neighborhood: "",
    website: "",
    instagram: "",
    facebook: "",
    linkedin: "",
    twitter: "",
    youtube: "",
    twitch: "",
    tiktok: "",
    bluesky: "",
  });
  const avatarFileRef = React.useRef<HTMLInputElement>(null);
  // The RTA subscription outlives individual renders. Keep its target mutable
  // so a conclude event saves the form currently on screen, not the empty
  // zero-initialized form captured while the organization request was loading.
  const saveOrgBasicInformationRef = React.useRef(saveOrgBasicInformation);
  saveOrgBasicInformationRef.current = saveOrgBasicInformation;
  const enterpriseRTAOrgAvatarUpdate = React.useMemo(
    () => createOrgAvatarUpdateHandler(saveOrgBasicInformationRef),
    [],
  );

  const { permissions } = usePermissions();

  const [phoneNumber, setPhoneNumber] = React.useState(0);
  const [phoneAreaCode, setPhoneAreaCode] = React.useState(0);
  const canEdit = checkPermission(permissions.orgInfos, "edit");

  function updateFormField(field: keyof typeof formFields, value: string) {
    setFormFields((prev) => ({ ...prev, [field]: value }));
  }

  React.useEffect(() => {
    // The cleanup returned from inside `.then()` is never wired by React, and an
    // empty-deps array was missing — so the listener leaked and re-subscribed on
    // every render. Mirror the isActive/captured-cleanup pattern used in
    // ads.tsx: register inside the resolved promise, expose the unsubscribe via
    // a captured `cleanup`, and run it from the effect's real teardown.
    let cleanup = () => {};
    let isActive = true;
    sessionInfo()
      .then((info) => {
        if (!isActive || !info) return;
        // Org-scoped room: an org logo is company-wide, and the compressor
        // conclude payload carries no uploader id, so enterprise-rta emits
        // enterpriseRTAOrgAvatarUpdate-<orgId> (no -<iss> suffix). The old
        // per-viewer name never matched the emitted room, so the swap only
        // showed after a reload.
        const eventName = `enterpriseRTAOrgAvatarUpdate-${info.org_id}`;
        RTA.on(eventName, enterpriseRTAOrgAvatarUpdate);
        cleanup = () => {
          RTA.off(eventName, enterpriseRTAOrgAvatarUpdate);
        };
      })
      .catch((err) => {
        console.error("Error retrieving session info:", err);
      });
    return () => {
      isActive = false;
      cleanup();
    };
  }, [enterpriseRTAOrgAvatarUpdate]);

  async function getOrganization() {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    try {
      const bandwidthMbps = getBandwidthMbps();
      const result = await enterpriseAPI.get("/get-organization", {
        headers: headers,
        params: { bandwidthMbps },
      });
      if (result.status === 200) {
        const data = result.data;
        // Normalise the phone ONCE, here, so the rendered value, the dirty
        // check (which compares organization.phone* against the form state)
        // and the PATCH payload can never disagree. See splitStoredOrgPhone:
        // organisations created through an access request have the full E.164
        // sitting in phoneNumber, which is why the row read "41 554197880145".
        const phone = splitStoredOrgPhone(data);
        setOrganization({
          name: data.name || "",
          avatar: data.avatar || "",
          cnpj: data.cnpj || "",
          duns: data.duns || "",
          email: data.email || "",
          phoneArea: phone.area,
          phoneCountry: data.phoneCountry || "",
          phoneNumber: phone.number,
          country: data.country || "",
          state: data.state || "",
          city: data.city || "",
          zipCode: data.zipCode || "",
          numberStreet: data.numberStreet || "",
          street: data.street || "",
          neighborhood: data.neighborhood || "",
          website: data.website || "",
          instagram: data.instagram || "",
          facebook: data.facebook || "",
          linkedin: data.linkedin || "",
          twitter: data.twitter || "",
          youtube: data.youtube || "",
          twitch: data.twitch || "",
          tiktok: data.tiktok || "",
          bluesky: data.bluesky || "",
        });
        setFormFields({
          cnpj: data.cnpj || "",
          duns: data.duns || "",
          email: data.email || "",
          country: data.country || "",
          state: data.state || "",
          city: data.city || "",
          zipCode: data.zipCode || "",
          numberStreet: data.numberStreet || "",
          street: data.street || "",
          neighborhood: data.neighborhood || "",
          website: data.website || "",
          instagram: data.instagram || "",
          facebook: data.facebook || "",
          linkedin: data.linkedin || "",
          twitter: data.twitter || "",
          youtube: data.youtube || "",
          twitch: data.twitch || "",
          tiktok: data.tiktok || "",
          bluesky: data.bluesky || "",
        });

        setPhoneAreaCode(Number(phone.area) || 0);
        setPhoneNumber(Number(phone.number) || 0);

        if (data.avatar) {
          setShowAvatar(data.avatar);
          setAvatarLoading(true);
        }
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  function previewAvatar() {
    const files = avatarFileRef.current?.files;
    if (!files) return;

    const reader = new FileReader();
    reader.onload = function () {
      setShowAvatar(reader.result as string);
      setAvatarLoading(true);
      setChangeBasicInfo(true);
    };
    reader.readAsDataURL(files[0]);
  }

  function cancelEditBasicInfos() {
    setFormFields({
      cnpj: organization.cnpj,
      duns: organization.duns,
      email: organization.email,
      country: organization.country,
      state: organization.state,
      city: organization.city,
      zipCode: organization.zipCode,
      numberStreet: organization.numberStreet,
      street: organization.street,
      neighborhood: organization.neighborhood,
      website: organization.website,
      instagram: organization.instagram,
      facebook: organization.facebook,
      linkedin: organization.linkedin,
      twitter: organization.twitter,
      youtube: organization.youtube,
      twitch: organization.twitch,
      tiktok: organization.tiktok,
      bluesky: organization.bluesky,
    });
    if (avatarFileRef.current) avatarFileRef.current.value = "";

    setPhoneAreaCode(Number(organization.phoneArea));
    setPhoneNumber(Number(organization.phoneNumber));
    setShowAvatar(organization.avatar);
    setAvatarLoading(!!organization.avatar);
    setChangeBasicInfo(false);
  }

  React.useEffect(() => {
    RTA.connect();
    getOrganization();
  }, []);

  // Detect form changes via effect instead of imperative DOM reads
  React.useEffect(() => {
    if (sendInProgress) {
      if (changeBasicInfo) setChangeBasicInfo(false);
      return;
    }

    if (
      Number(organization.cnpj) === Number(formFields.cnpj) &&
      Number(organization.duns) === Number(formFields.duns) &&
      organization.email === formFields.email &&
      Number(organization.phoneNumber) === Number(phoneNumber) &&
      Number(organization.phoneArea) === Number(phoneAreaCode) &&
      organization.country === formFields.country &&
      organization.state === formFields.state &&
      organization.city === formFields.city &&
      organization.zipCode === formFields.zipCode &&
      organization.numberStreet === formFields.numberStreet &&
      organization.street === formFields.street &&
      organization.neighborhood === formFields.neighborhood &&
      organization.website === formFields.website &&
      organization.instagram === formFields.instagram &&
      organization.facebook === formFields.facebook &&
      organization.linkedin === formFields.linkedin &&
      organization.twitter === formFields.twitter &&
      organization.youtube === formFields.youtube &&
      organization.twitch === formFields.twitch &&
      organization.tiktok === formFields.tiktok &&
      organization.bluesky === formFields.bluesky &&
      !avatarFileRef.current?.files?.length
    ) {
      setChangeBasicInfo(false);
      return;
    }

    setChangeBasicInfo(true);
  }, [formFields, phoneNumber, phoneAreaCode, sendInProgress]);

  async function prepareSaveOrgBasicInformation() {
    const normalizedEmail = formFields.email.trim();
    if (validateField.email(normalizedEmail) instanceof Error) {
      setTypeBasicFeedback("error");
      setBasicFeedback(true);
      setBasicMessage("Informe um e-mail válido antes de salvar as informações da organização");
      return false;
    }

    const avatar = avatarFileRef.current?.files;
    if (!avatar || avatar.length === 0) {
      saveOrgBasicInformation(undefined);
      return false;
    }

    setChangeBasicInfo(false);
    setSendInProgress(true);
    setTypeBasicFeedback("info");
    setBasicFeedback(true);
    setBasicMessage("Enviando os dados, aguarde...");
    const mediaId = crypto.randomUUID();
    const res = await enterpriseAPI.post(
      "/create-ticket-org",
      {
        expectedMedias: 1,
        mediaId: mediaId,
      },
      {
        headers: {
          "X-JWT": (await localforage.getItem("JWT")) as string,
        },
      },
    );

    if (res.status !== 200) {
      console.error("Failed to create ticket");
      setTypeBasicFeedback("error");
      setBasicMessage("Erro ao tentar atualizar informações básicas");
      return false;
    }

    const { promise } = createTurboUpload({
      ticketId: res.data.ticketId,
      file: avatar[0],
      mediaId: mediaId,
      mediaType: "image",
      section: "orgAvatar",
      parallel: 4,
      getToken: () => Promise.resolve(localforage.getItem("JWT") as Promise<string>),
    });

    const result = await promise;
    if (result.ok) {
      return false;
    }

    if (result.backlog) {
      // Backlog cap: a friendly "na fila" notice, never a red error.
      setTypeBasicFeedback("info");
      setBasicMessage(UPLOAD_BACKLOG_NOTICE);
      return false;
    }

    console.error(result);
    let bannerMessage = "Erro ao tentar atualizar informações básicas";
    setTypeBasicFeedback("error");
    const errorMessages: { [key: number]: string } = {
      400: "Arquivo de avatar inválido",
    };

    bannerMessage =
      errorMessages[result.status] ||
      getUserFacingError(result, { context: "upload", fallback: bannerMessage });
    setBasicMessage(bannerMessage);
    return false;
  }

  async function saveOrgBasicInformation(avatar: string | undefined) {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    const body: { [key: string]: any } = {
      email: formFields.email.trim(),
      country: formFields.country,
      state: formFields.state,
      city: formFields.city,
      zipCode: formFields.zipCode,
      numberStreet: formFields.numberStreet,
      street: formFields.street,
      neighborhood: formFields.neighborhood,
      website: formFields.website,
      instagram: formFields.instagram,
      facebook: formFields.facebook,
      linkedin: formFields.linkedin,
      twitter: formFields.twitter,
      youtube: formFields.youtube,
      twitch: formFields.twitch,
      tiktok: formFields.tiktok,
      bluesky: formFields.bluesky,
      // phoneArea/phoneNumber must be strings and the logo travels under
      // avatarManifestUrl. Sending numeric phones or an `avatar` key made
      // /update-org answer 400 "Informações mal inseridas" while the logo had
      // already been saved by the compressor-conclude. See buildOrgContactAvatarFields.
      ...buildOrgContactAvatarFields({
        phoneAreaCode,
        phoneNumber,
        organizationCountry: formFields.country,
        phoneCountry: organization.phoneCountry,
        avatarManifestUrl: avatar,
        currentAvatar: organization.avatar,
      }),
    };

    try {
      const result = await enterpriseAPI.patch("/update-org", body, {
        headers: headers,
      });
      if (result.status === 200) {
        // Keep the gate flag the route loaders read (index.tsx) in sync with
        // reality the moment the logo is saved — otherwise `orgHasAvatar` stays
        // stale-false until the next full auth refresh (30-min TTL) and clicking
        // Anúncios/Negociar keeps bouncing to /organizacao/info.
        await localforage.setItem("orgHasAvatar", !!body.avatarManifestUrl);
        setBasicMessage("Informações básicas atualizadas com sucesso");
        setTypeBasicFeedback("success");
        setBasicFeedback(true);
        setChangeBasicInfo(false);
        setOrganization({
          name: organization.name,
          avatar: body.avatarManifestUrl,
          cnpj: organization.cnpj,
          duns: organization.duns,
          email: body.email,
          phoneCountry: body.phoneCountry,
          phoneArea: body.phoneArea,
          phoneNumber: body.phoneNumber,
          country: formFields.country,
          state: formFields.state,
          city: formFields.city,
          zipCode: formFields.zipCode,
          numberStreet: formFields.numberStreet,
          street: formFields.street,
          neighborhood: formFields.neighborhood,
          website: formFields.website,
          instagram: formFields.instagram,
          facebook: formFields.facebook,
          linkedin: formFields.linkedin,
          twitter: formFields.twitter,
          youtube: formFields.youtube,
          twitch: formFields.twitch,
          tiktok: formFields.tiktok,
          bluesky: formFields.bluesky,
        });
        return;
      }
    } catch (error: any) {
      let bannerMessage = getUserFacingError(error, {
        fallback: "Não foi possível atualizar os dados da empresa. Tente novamente em instantes.",
      });
      setTypeBasicFeedback("error");
      setBasicFeedback(true);

      if (!error.response) {
        setBasicMessage(bannerMessage);
        return;
      }

      const errorMessages: { [key: number]: string } = {
        400: "Confira o nome, o e-mail, o telefone e os demais dados da empresa antes de salvar.",
        403: "Você não tem permissão para alterar os dados da empresa. Fale com o administrador.",
      };

      bannerMessage = errorMessages[error.response.status] || bannerMessage;
      setBasicMessage(bannerMessage);
    } finally {
      setSendInProgress(false);
    }
  }

  return (
    <>
      <BannerFeedback
        type={typeBasicFeedback}
        open={openBasicFeedback}
        message={basicMessage}
        callback={() => setBasicFeedback(false)}
        sx={{
          marginTop: "var(--spacing-2xl)",
          maxWidth: "var(--width-xl)",
        }}
      />
      <div id="orgAvatarAndTitle">
        <label id="orgAvatar" htmlFor="orgSetAvatar">
          <div id="defaultAvatar">
            <AddRoundedIcon sx={{ fontSize: "72px" }} />
            <span>Alterar Avatar</span>
          </div>
          {avatarLoading && showAvatar && (
            <Skeleton
              variant="circular"
              width="100%"
              height="100%"
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "var(--border-radius-full)",
                margin: "0px !important",
              }}
            />
          )}
          <input
            type="file"
            accept="image/*"
            id="orgSetAvatar"
            ref={avatarFileRef}
            onChange={previewAvatar}
            disabled={sendInProgress || !canEdit}
            hidden
          />
          <Avatar
            sx={avatarSX}
            src={resolvedShowAvatar}
            id="orgPreviewAvatar"
            style={{ opacity: avatarLoading ? 0 : 1 }}
            slotProps={{
              img: {
                onLoad: () => setTimeout(() => setAvatarLoading(false), 250),
                onError: () => {
                  console.error("Error loading org avatar image");
                  setAvatarLoading(false);
                },
              },
            }}
          />
        </label>
        <h2 id="orgTitle">{organization.name}</h2>
      </div>
      <div id="orgContainers">
        <div className="orgBasicInfo">
          <div className="orgBasicInfoField">
            <span>CNPJ:</span>
            <input
              id="orgCNPJ"
              type="text"
              placeholder="CNPJ"
              value={formFields.cnpj}
              onChange={(e) => updateFormField("cnpj", e.target.value)}
              disabled
            />
          </div>
          <div className="orgBasicInfoField">
            <span>DUNS: </span>
            <input
              id="orgDUNS"
              type="text"
              placeholder="DUNS"
              value={formFields.duns}
              onChange={(e) => updateFormField("duns", e.target.value)}
              disabled
            />
          </div>
          <div className="orgBasicInfoField">
            <span>Email: </span>
            <input
              id="orgEmail"
              type="email"
              placeholder="Email"
              value={formFields.email}
              onChange={(e) => updateFormField("email", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <span>Telefone: </span>
            <InputTel
              id="orgPhone"
              areaCode={organization.phoneArea ? Number(organization.phoneArea) : 0}
              number={organization.phoneNumber ? Number(organization.phoneNumber) : 0}
              disabled={sendInProgress || !canEdit}
              onChange={(areaCode, number) => {
                setPhoneAreaCode(areaCode);
                setPhoneNumber(number);
              }}
            />
          </div>
          <div className="orgBasicInfoField">
            <span>Website: </span>
            <input
              id="orgWebsite"
              type="text"
              placeholder="Website"
              value={formFields.website}
              onChange={(e) => updateFormField("website", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
        </div>
        <div className="orgBasicInfo">
          <div className="orgBasicInfoField">
            <span>País: </span>
            <input
              id="orgCountry"
              type="text"
              placeholder="País"
              value={formFields.country}
              onChange={(e) => updateFormField("country", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <span>Estado: </span>
            <input
              id="orgState"
              type="text"
              placeholder="Estado"
              value={formFields.state}
              onChange={(e) => updateFormField("state", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <span>Cidade: </span>
            <input
              id="orgCity"
              type="text"
              placeholder="Cidade"
              value={formFields.city}
              onChange={(e) => updateFormField("city", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <span>Rua: </span>
            <input
              id="orgStreet"
              type="text"
              placeholder="Rua"
              value={formFields.street}
              onChange={(e) => updateFormField("street", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <span>Número: </span>
            <input
              id="orgNumberStreet"
              type="text"
              placeholder="Número"
              value={formFields.numberStreet}
              onChange={(e) => updateFormField("numberStreet", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <span>Bairro: </span>
            <input
              id="orgNeighborhood"
              type="text"
              placeholder="Bairro"
              value={formFields.neighborhood}
              onChange={(e) => updateFormField("neighborhood", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <span>CEP: </span>
            <input
              id="orgZipCode"
              type="text"
              placeholder="CEP"
              value={formFields.zipCode}
              onChange={(e) => updateFormField("zipCode", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
        </div>
        <div className="orgBasicInfo">
          <div className="orgBasicInfoField">
            <FontAwesomeIcon icon={faInstagram} />
            <input
              id="orgInstagram"
              type="text"
              placeholder="Instagram"
              value={formFields.instagram}
              onChange={(e) => updateFormField("instagram", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <FontAwesomeIcon icon={faFacebook} />
            <input
              id="orgFacebook"
              type="text"
              placeholder="Facebook"
              value={formFields.facebook}
              onChange={(e) => updateFormField("facebook", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <FontAwesomeIcon icon={faLinkedin} />
            <input
              id="orgLinkedin"
              type="text"
              placeholder="LinkedIn"
              value={formFields.linkedin}
              onChange={(e) => updateFormField("linkedin", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <FontAwesomeIcon icon={faXTwitter} />
            <input
              id="orgTwitter"
              type="text"
              placeholder="Twitter"
              value={formFields.twitter}
              onChange={(e) => updateFormField("twitter", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <FontAwesomeIcon icon={faYoutube} />
            <input
              id="orgYoutube"
              type="text"
              placeholder="YouTube"
              value={formFields.youtube}
              onChange={(e) => updateFormField("youtube", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <FontAwesomeIcon icon={faTwitch} />
            <input
              id="orgTwitch"
              type="text"
              placeholder="Twitch"
              value={formFields.twitch}
              onChange={(e) => updateFormField("twitch", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <FontAwesomeIcon icon={faTiktok} />
            <input
              id="orgTiktok"
              type="text"
              placeholder="TikTok"
              value={formFields.tiktok}
              onChange={(e) => updateFormField("tiktok", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
          <div className="orgBasicInfoField">
            <FontAwesomeIcon icon={faBluesky} />
            <input
              id="orgBluesky"
              type="text"
              placeholder="Bluesky"
              value={formFields.bluesky}
              onChange={(e) => updateFormField("bluesky", e.target.value)}
              disabled={sendInProgress || !canEdit}
            />
          </div>
        </div>
      </div>
      <Collapse in={changeBasicInfo}>
        <div id="orgBasicInfoActions">
          <button
            id="orgBtnSaveBasicInfos"
            onClick={prepareSaveOrgBasicInformation}
            disabled={sendInProgress || !canEdit}
          >
            Salvar
          </button>
          <button id="orgBtnResetBasicInfos" onClick={cancelEditBasicInfos}>
            Cancelar
          </button>
        </div>
      </Collapse>
    </>
  );
}

type RoleListItem = {
  id: string;
  name: string;
  permissions: RolePermissions;
  memberCount: number;
  updatedAt: string;
  updatedBy?: { id: string; name: string };
  icon?: string;
};

function emptyPermissionsValue(): RolePermissions {
  return {
    orgInfos: [],
    orgCollaborators: [],
    orgRoles: [],
    orgPayments: [],
    invoices: [],
    dashboard: [],
    support: [],
    media: [],
    auditLog: [],
    contact: [],
    hall: [],
    roomRadioAds: [],
    roomTvAds: [],
    roomChatAds: [],
    hallAds: [],
    bid: [],
    handleNegotiateConfigs: [],
    report: [],
  };
}

function inferIcon(name: string): SvgIconComponent {
  const match = ROLE_TEMPLATES.find((t) => t.label === name || t.key === name);
  return match?.icon ?? DEFAULT_ROLE_ICON;
}

function countPermissions(perms: RolePermissions | null | undefined): number {
  if (!perms || typeof perms !== "object") return 0;
  let n = 0;
  for (const key of Object.keys(perms) as (keyof RolePermissions)[]) {
    const v = perms[key];
    if (Array.isArray(v)) n += v.length;
  }
  return n;
}

export function IAM() {
  const [roles, setRoles] = React.useState<RoleListItem[]>([]);
  const [search, setSearch] = React.useState("");
  const [openBasicFeedback, setBasicFeedback] = React.useState(false);
  const [typeBasicFeedback, setTypeBasicFeedback] = React.useState<
    "error" | "success" | "warning" | "info"
  >("error");
  const [basicMessage, setBasicMessage] = React.useState("");

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerMode, setDrawerMode] = React.useState<DrawerMode>("view");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [drawerName, setDrawerName] = React.useState("");
  const [drawerPerms, setDrawerPerms] = React.useState<RolePermissions>(emptyPermissionsValue());
  const initialDrawerSnapshot = React.useRef<{ name: string; perms: RolePermissions } | null>(null);

  const [menuAnchor, setMenuAnchor] = React.useState<{ el: HTMLElement; roleId: string } | null>(
    null,
  );

  const { permissions } = usePermissions();
  const canCreate = checkPermission(permissions.orgRoles, "create");
  const canEdit = checkPermission(permissions.orgRoles, "edit");
  const canDelete = checkPermission(permissions.orgRoles, "delete");

  async function loadRoles() {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return;
    try {
      const res = await enterpriseAPI.get<Partial<RoleListItem>[]>("/list-roles", {
        headers: { "X-JWT": JWT },
      });
      // First pass: normalize whatever the legacy endpoint returned (id+name only)
      // so the list renders immediately. Counters show 0 until the enrichment
      // step below fills permissions in.
      const normalized = (res.data || []).map(
        (r): RoleListItem => ({
          id: r.id ?? "",
          name: r.name ?? "",
          permissions: (r.permissions ?? emptyPermissionsValue()) as RolePermissions,
          memberCount: r.memberCount ?? 0,
          updatedAt: r.updatedAt ?? "",
          updatedBy: r.updatedBy,
          icon: r.icon,
        }),
      );
      setRoles(normalized);

      // Second pass: legacy /list-roles omits permissions, so fan out one
      // /get-role per role in parallel and patch the cards as they resolve.
      // Drops to a no-op once Phase A4 (enriched /list-roles) deploys, because
      // permissions will already be populated and `cardinality === 0` rare.
      const enriched = await Promise.all(
        normalized.map(async (r): Promise<RoleListItem> => {
          if (!r.id) return r;
          try {
            const detail = await enterpriseAPI.get<{
              permissions?: Partial<RolePermissions>;
            }>("/get-role", {
              headers: { "X-JWT": JWT },
              params: { id: r.id },
            });
            return {
              ...r,
              permissions: {
                ...emptyPermissionsValue(),
                ...(detail.data?.permissions ?? {}),
              },
            };
          } catch {
            return r;
          }
        }),
      );
      setRoles(enriched);
    } catch (e) {
      console.error("loadRoles failed", e);
      setRoles([]);
    }
  }

  React.useEffect(() => {
    loadRoles();
  }, []);

  const filtered = roles.filter((r) => r.name.toLowerCase().includes(search.trim().toLowerCase()));

  function isDirty(): boolean {
    if (!initialDrawerSnapshot.current) return drawerName.trim().length > 0;
    return (
      initialDrawerSnapshot.current.name !== drawerName ||
      JSON.stringify(initialDrawerSnapshot.current.perms) !== JSON.stringify(drawerPerms)
    );
  }

  function openDrawerCreate(template?: RoleTemplate) {
    const perms = template ? { ...template.permissions } : emptyPermissionsValue();
    const name = template ? template.label : "";
    setDrawerMode("create");
    setEditingId(null);
    setDrawerName(name);
    setDrawerPerms(perms);
    initialDrawerSnapshot.current = { name, perms };
    setDrawerOpen(true);
  }

  // Fetches enriched role detail via /get-role and updates local list so future
  // opens are instant. The /list-roles endpoint (deployed BE) only returns
  // {id, name} — permissions arrive only from /get-role.
  async function fetchRoleDetail(id: string): Promise<RolePermissions | null> {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return null;
    try {
      const res = await enterpriseAPI.get<{
        id: string;
        name: string;
        permissions?: Partial<RolePermissions>;
      }>("/get-role", {
        headers: { "X-JWT": JWT },
        params: { id },
      });
      const merged: RolePermissions = {
        ...emptyPermissionsValue(),
        ...(res.data?.permissions ?? {}),
      };
      setRoles((rs) => rs.map((r) => (r.id === id ? { ...r, permissions: merged } : r)));
      return merged;
    } catch (e) {
      console.error("get-role failed", e);
      return null;
    }
  }

  async function openDrawerView(role: RoleListItem) {
    // Open immediately with whatever is cached so the UX is snappy.
    setDrawerMode("view");
    setEditingId(role.id);
    setDrawerName(role.name);
    setDrawerPerms(role.permissions);
    initialDrawerSnapshot.current = { name: role.name, perms: role.permissions };
    setDrawerOpen(true);
    // Then fetch enriched permissions and swap in.
    const fresh = await fetchRoleDetail(role.id);
    if (fresh) {
      setDrawerPerms(fresh);
      initialDrawerSnapshot.current = { name: role.name, perms: fresh };
    }
  }

  function switchToEdit() {
    setDrawerMode("edit");
    initialDrawerSnapshot.current = { name: drawerName, perms: drawerPerms };
  }

  function cloneCurrent() {
    setDrawerMode("create");
    setEditingId(null);
    const cloneName = `${drawerName} (cópia)`;
    setDrawerName(cloneName);
    initialDrawerSnapshot.current = { name: cloneName, perms: drawerPerms };
  }

  async function deleteCurrent() {
    if (!editingId) return;
    if (!window.confirm(`Excluir o cargo "${drawerName}"?`)) return;
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    try {
      await enterpriseAPI.delete("/delete-role", {
        headers: { "X-JWT": JWT },
        params: { id: editingId },
      });
      setBasicFeedback(true);
      setTypeBasicFeedback("success");
      setBasicMessage("Cargo excluído.");
      setDrawerOpen(false);
      loadRoles();
    } catch (e) {
      setBasicFeedback(true);
      setTypeBasicFeedback("error");
      setBasicMessage(
        getUserFacingError(e, {
          fallback: "Não foi possível excluir este cargo. Tente novamente em instantes.",
        }),
      );
    }
  }

  async function saveDrawer() {
    if (!drawerName.trim()) return;
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    try {
      if (drawerMode === "create") {
        await enterpriseAPI.post(
          "/create-role",
          { name: drawerName, permissions: drawerPerms },
          { headers: { "X-JWT": JWT } },
        );
      } else if (drawerMode === "edit" && editingId) {
        await enterpriseAPI.put(
          "/update-role",
          { newName: drawerName, newPermissions: drawerPerms, targetId: editingId },
          { headers: { "X-JWT": JWT } },
        );
      }
      setBasicFeedback(true);
      setTypeBasicFeedback("success");
      setBasicMessage(drawerMode === "create" ? "Cargo criado." : "Cargo atualizado.");
      setDrawerOpen(false);
      loadRoles();
    } catch (e: any) {
      setBasicFeedback(true);
      setTypeBasicFeedback("error");
      const status = e?.response?.status;
      if (status === 403) {
        setBasicMessage("Sem permissão para esta ação.");
      } else {
        setBasicMessage(
          getUserFacingError(e, {
            fallback: "Não foi possível salvar este cargo. Tente novamente em instantes.",
          }),
        );
      }
    }
  }

  return (
    <>
      <Title
        title="Cargos"
        subtitle="Os cargos atribuem permissões nas ferramentas da plataforma"
      />
      <BannerFeedback
        type={typeBasicFeedback}
        open={openBasicFeedback}
        message={basicMessage}
        callback={() => setBasicFeedback(false)}
        sx={{ marginTop: "var(--spacing-2xl)", maxWidth: "var(--width-xl)" }}
      />

      <div id="iamHeader">
        <div id="iamSearchWrap">
          <SearchIcon className="iamSearchIcon" />
          <input
            id="iamSearch"
            type="text"
            placeholder="Pesquisar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {canCreate && (
          <button id="iamAddRole" onClick={() => openDrawerCreate()} type="button">
            <AddIcon fontSize="small" />
            Adicionar Cargo
          </button>
        )}
      </div>

      {canCreate && <TemplateChips onPickTemplate={openDrawerCreate} />}

      <div id="iamRoleGrid">
        {filtered.length === 0 && (
          <div id="iamEmpty">
            <BadgeTwoToneIcon className="iamEmptyIcon" />
            <p>Nenhum cargo encontrado</p>
            {canCreate && (
              <button onClick={() => openDrawerCreate()} type="button">
                <AddIcon fontSize="small" />
                Criar primeiro cargo
              </button>
            )}
          </div>
        )}
        {filtered.map((r) => (
          <RoleCard
            key={r.id}
            icon={inferIcon(r.name)}
            name={r.name}
            memberCount={r.memberCount}
            permissionCount={countPermissions(r.permissions)}
            updatedAt={r.updatedAt}
            updatedByName={r.updatedBy?.name}
            onClick={() => openDrawerView(r)}
            onMenuClick={(anchor) => setMenuAnchor({ el: anchor, roleId: r.id })}
          />
        ))}
      </div>

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={async () => {
            const r = roles.find((x) => x.id === menuAnchor!.roleId);
            setMenuAnchor(null);
            if (r) {
              await openDrawerView(r);
              switchToEdit();
            }
          }}
          disabled={!canEdit}
        >
          Editar
        </MenuItem>
        <MenuItem
          onClick={async () => {
            const r = roles.find((x) => x.id === menuAnchor!.roleId);
            setMenuAnchor(null);
            if (r) {
              const fresh = (await fetchRoleDetail(r.id)) ?? r.permissions;
              setDrawerMode("create");
              setEditingId(null);
              const cloneName = `${r.name} (cópia)`;
              setDrawerName(cloneName);
              setDrawerPerms(fresh);
              initialDrawerSnapshot.current = { name: cloneName, perms: fresh };
              setDrawerOpen(true);
            }
          }}
          disabled={!canCreate}
        >
          Clonar
        </MenuItem>
        <MenuItem
          onClick={() => {
            const r = roles.find((x) => x.id === menuAnchor!.roleId);
            setMenuAnchor(null);
            if (r) {
              openDrawerView(r);
              setTimeout(deleteCurrent, 0);
            }
          }}
          disabled={!canDelete}
        >
          Excluir
        </MenuItem>
      </Menu>

      <RoleDrawer
        open={drawerOpen}
        mode={drawerMode}
        name={drawerName}
        permissions={drawerPerms}
        memberCount={editingId ? roles.find((r) => r.id === editingId)?.memberCount : undefined}
        dirty={isDirty()}
        onClose={() => setDrawerOpen(false)}
        onNameChange={setDrawerName}
        onPermissionsChange={setDrawerPerms}
        onSave={saveDrawer}
        onSwitchToEdit={switchToEdit}
        onClone={cloneCurrent}
        onDelete={deleteCurrent}
      />
    </>
  );
}

export function Collaborators() {
  const [collaborators, setCollaborators] = React.useState<CollaboratorPayload[]>([]);
  const [owners, setOwners] = React.useState<OwnerPayload[]>([]);
  const [pendingInvites, setPendingInvites] = React.useState<PendingInvitePayload[]>([]);
  const [currentCollaboratorID, setCurrentCollaboratorID] = React.useState("");
  const [selectCollaborator, setSelectCollaborator] = React.useState<CollaboratorPayload | null>(
    null,
  );
  const [detailsAction, setDetailsAction] = React.useState<"view" | "create" | "edit" | "grant">(
    "view",
  );
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [search, setSearch] = React.useState("");
  const [bannerFeedbackIsOpen, setBannerFeedbackIsOpen] = React.useState(false);
  const [bannerFeedbackType, setBannerFeedbackType] = React.useState<
    "error" | "success" | "warning" | "info"
  >("error");
  const [bannerFeedbackMessage, setBannerFeedbackMessage] = React.useState("");
  const colEmailRef = React.useRef<string>("");
  const openMenu = Boolean(anchorEl);
  const { permissions } = usePermissions();
  const canViewCollaborators = checkPermission(permissions.orgCollaborators, "view");

  class handleMenu {
    static open(event: React.MouseEvent<HTMLElement>) {
      const selectCol = collaborators.filter((col) => col.id === event.currentTarget.id);
      setAnchorEl(event.currentTarget);
      setSelectCollaborator(selectCol[0]);
    }

    static close() {
      setAnchorEl(null);
    }
  }

  class backend {
    static async prepareJWT() {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (!JWT) return false;

      const headers: { [key: string]: string | undefined } = {
        "X-JWT": JWT,
      };

      return headers;
    }

    static async resetListCollaborators() {
      const headers = await backend.prepareJWT();
      if (!headers) return false;

      const collaborators: CollaboratorPayload[] = [];
      for (let page = 0; ; page++) {
        const pageQuery = `?page=${page}`;
        try {
          const result = await enterpriseAPI.get(`/list-collaborators${pageQuery}`, {
            headers: headers,
          });
          if (result.status === 200) {
            collaborators.push(...result.data);
            if (result.data.length === 0) {
              setCollaborators(collaborators);
              break;
            }
          }
        } catch (error) {
          console.error(error);
          return false;
        }
      }
    }

    static async listOwners() {
      const headers = await backend.prepareJWT();
      if (!headers) return false;

      const owners: OwnerPayload[] = [];
      for (let page = 0; ; page++) {
        const pageQuery = `?page=${page}`;
        try {
          const result = await enterpriseAPI.get(`/list-owners${pageQuery}`, {
            headers: headers,
          });
          if (result.status === 200) {
            owners.push(...result.data);
            if (result.data.length === 0) {
              setOwners(owners);
              break;
            }
          }
        } catch (error) {
          console.error(error);
          return false;
        }
      }
    }

    static async listPendingInvites() {
      const headers = await backend.prepareJWT();
      if (!headers || !canViewCollaborators) return false;

      try {
        const result = await enterpriseAPI.get<PendingInvitePayload[]>("/pending-invites", {
          headers,
          params: { search },
        });
        setPendingInvites(result.data ?? []);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    static async inviteCollaborator() {
      const headers = await backend.prepareJWT();
      if (!headers) return false;

      const body = {
        email: colEmailRef.current,
      };

      try {
        const result = await enterpriseAPI.post("/invite", body, {
          headers: headers,
        });
        if (result.status === 200 || result.status === 204) {
          setBannerFeedbackIsOpen(true);
          setBannerFeedbackType("success");
          setBannerFeedbackMessage("Convite enviado com sucesso");
          await backend.listPendingInvites();
        }
      } catch (e: any) {
        console.error(e);
        setBannerFeedbackIsOpen(true);
        setBannerFeedbackType("error");
        let bannerMessage = getUserFacingError(e, {
          fallback: "Não foi possível convidar o colaborador. Tente novamente em instantes.",
        });
        if (!e.response) {
          setBannerFeedbackMessage(bannerMessage);
          return;
        }

        const errorTable: { [key: number]: string } = {
          400: "Confira os dados do colaborador e os cargos escolhidos antes de enviar o convite.",
          403: "Sem permissão para convidar colaborador",
        };

        bannerMessage = errorTable[e.response.status] || bannerMessage;
        setBannerFeedbackMessage(bannerMessage);
        return;
      } finally {
        setDetailsOpen(false);
        setSelectCollaborator(null);
      }
    }

    static async deleteCollaborator(id: string | undefined) {
      if (!id) return false;

      const headers = await backend.prepareJWT();
      if (!headers) return false;

      const session = await sessionInfo();
      if (!session) return false;

      for (let i = 0; i < collaborators.length; i++) {
        if (collaborators[i].id !== id) continue;

        if (collaborators[i].isOwner && id !== session.iss) {
          setBannerFeedbackIsOpen(true);
          setBannerFeedbackType("error");
          setBannerFeedbackMessage("Não é possível deletar um dono da organização");
          return;
        }
      }

      const idQuery = `?id=${id}`;
      try {
        const result = await enterpriseAPI.delete(`/delete-collaborator${idQuery}`, {
          headers: headers,
        });
        if (result.status === 200) {
          setBannerFeedbackIsOpen(true);
          setBannerFeedbackType("success");
          setBannerFeedbackMessage("Colaborador deletado com sucesso");
          await backend.resetListCollaborators();
        }
      } catch (e: any) {
        console.error(e);
        if (!e.response) {
          setBannerFeedbackIsOpen(true);
          setBannerFeedbackType("error");
          setBannerFeedbackMessage(
            getUserFacingError(e, {
              context: "general",
              fallback: "Erro ao tentar deletar colaborador",
            }),
          );
          return;
        }

        if (e.response.status === 403) {
          setBannerFeedbackIsOpen(true);
          setBannerFeedbackType("error");
          setBannerFeedbackMessage("Sem permissão para deletar colaborador");
          return;
        }

        setBannerFeedbackIsOpen(true);
        setBannerFeedbackType("error");
        setBannerFeedbackMessage(
          getUserFacingError(e, {
            context: "general",
            fallback: "Erro ao tentar deletar colaborador",
          }),
        );
        return;
      }
    }

    static async revokeInvite(id: string) {
      const headers = await backend.prepareJWT();
      if (!headers) return false;
      try {
        await enterpriseAPI.delete("/invite", { headers, params: { id } });
        setPendingInvites((items) => items.filter((item) => item.id !== id));
        setBannerFeedbackType("success");
        setBannerFeedbackMessage("Convite revogado com sucesso");
        setBannerFeedbackIsOpen(true);
        return true;
      } catch (error) {
        console.error(error);
        setBannerFeedbackType("error");
        setBannerFeedbackMessage(
          getUserFacingError(error, {
            context: "general",
            fallback: "Não foi possível revogar o convite",
          }),
        );
        setBannerFeedbackIsOpen(true);
        return false;
      }
    }

    static async transferOwnership(target: CollaboratorPayload | null) {
      if (
        !target ||
        !window.confirm(
          `Transferir a administração para ${target.name}? Você deixará de ser administrador.`,
        )
      ) {
        return false;
      }
      const headers = await backend.prepareJWT();
      if (!headers) return false;
      try {
        await enterpriseAPI.post(
          "/transfer-ownership",
          { targetCollaboratorId: target.id },
          { headers },
        );
        await Promise.all([backend.resetListCollaborators(), backend.listOwners()]);
        setBannerFeedbackType("success");
        setBannerFeedbackMessage("Propriedade transferida com sucesso");
        setBannerFeedbackIsOpen(true);
        handleMenu.close();
        return true;
      } catch (error) {
        console.error(error);
        setBannerFeedbackType("error");
        setBannerFeedbackMessage(
          getUserFacingError(error, {
            context: "general",
            fallback: "Não foi possível transferir a propriedade",
          }),
        );
        setBannerFeedbackIsOpen(true);
        return false;
      }
    }
  }

  function BuildCollaboratorDetailContent() {
    const [allRoles, setAllRoles] = React.useState<RoleProps[]>([]);
    const [roleName, setRoleName] = React.useState("");
    const [possibleRoles, setPossibleRoles] = React.useState<string[]>([]);
    const [createInviteCollaboratorHasValidEmail, setCreateInviteCollaboratorHasValidEmail] =
      React.useState(false);

    function onInputEmail(e: React.FormEvent<HTMLInputElement>) {
      const value = (e.target as HTMLInputElement).value;
      if (validateField.email(value) instanceof Error) {
        setCreateInviteCollaboratorHasValidEmail(false);
        return;
      }

      setCreateInviteCollaboratorHasValidEmail(true);
    }

    class Role {
      static async list() {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (!JWT) return false;

        const headers: { [key: string]: string | undefined } = {
          "X-JWT": JWT,
        };

        let query = "";
        if (roleName) {
          query = `?search=${roleName}`;
        }

        try {
          const result = await enterpriseAPI.get(`/list-roles${query}`, {
            headers: headers,
          });
          const data = result.data as RoleProps[];
          if (!data) {
            setAllRoles([]);
            return;
          }

          const allRolesUnlessAlreadyIncluded = [];
          for (let i = 0; i < data.length; i++) {
            let isAlreadyIncluded = false;
            for (let j = 0; j < (selectCollaborator?.roles?.length ?? 0); j++) {
              if (data[i].id === selectCollaborator?.roles[j].id) {
                isAlreadyIncluded = true;
                break;
              }
            }

            if (!isAlreadyIncluded) {
              allRolesUnlessAlreadyIncluded.push(data[i]);
            }
          }

          setAllRoles(allRolesUnlessAlreadyIncluded);
        } catch (error) {
          console.error(error);
        }
      }

      static async grant() {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (!JWT) return false;

        const headers: { [key: string]: string | undefined } = {
          "X-JWT": JWT,
        };

        const body = {
          collaboratorId: selectCollaborator?.id,
          roleIds: possibleRoles,
        };

        try {
          await enterpriseAPI.patch("/grant-roles", body, { headers: headers });
          if (!selectCollaborator) return;
          const selRoles = selectCollaborator.roles || [];
          const newRls = allRoles.filter((role) => possibleRoles.includes(role.id));
          selectCollaborator.roles = [...selRoles, ...newRls];
          setSelectCollaborator({ ...selectCollaborator });
          setDetailsAction("edit");
          RTA.emit(`resetPermissions-${selectCollaborator.orgId}-${selectCollaborator.id}`);
        } catch (error) {
          console.error(error);
        }
      }

      static async revoke(id: string) {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (!JWT) return false;

        const headers: { [key: string]: string | undefined } = {
          "X-JWT": JWT,
        };

        const body = {
          collaboratorId: selectCollaborator?.id,
          roleIds: [id],
        };

        try {
          await enterpriseAPI.patch("/revoke-roles", body, {
            headers: headers,
          });
          if (!selectCollaborator) return;
          selectCollaborator.roles = selectCollaborator.roles.filter((role) => role.id !== id);
          setSelectCollaborator({ ...selectCollaborator });
          RTA.emit(`resetPermissions-${selectCollaborator.orgId}-${selectCollaborator.id}`);
        } catch (error) {
          console.error(error);
        }
      }
    }

    async function editCollaborator() {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (!JWT) return false;

      const headers: { [key: string]: string | undefined } = {
        "X-JWT": JWT,
      };

      if (!selectCollaborator?.id) return false;

      const body = {
        id: selectCollaborator.id,
        email: colEmailRef.current,
      };

      try {
        const result = await enterpriseAPI.patch("/update-collaborator-restrict", body, {
          headers: headers,
        });
        if (result.status === 200) {
          setCollaborators(
            collaborators.map((col) => {
              if (col.id === selectCollaborator?.id) {
                return {
                  ...col,
                  ...body,
                };
              }
              return col;
            }),
          );
          setBannerFeedbackIsOpen(true);
          setBannerFeedbackType("success");
          setBannerFeedbackMessage("Colaborador atualizado com sucesso");
        }
      } catch (e: any) {
        console.error(e);
        setBannerFeedbackIsOpen(true);
        setBannerFeedbackType("error");
        let bannerMessage = getUserFacingError(e, {
          fallback: "Não foi possível atualizar este colaborador. Tente novamente em instantes.",
        });
        if (!e.response) {
          setBannerFeedbackMessage(bannerMessage);
          return;
        }

        const errorTable: { [key: number]: string } = {
          400: "Confira os dados do colaborador e os cargos escolhidos antes de salvar.",
          403: "Sem permissão para atualizar colaborador",
        };

        bannerMessage = errorTable[e.response.status] || bannerMessage;
        setBannerFeedbackMessage(bannerMessage);
        return;
      } finally {
        setDetailsOpen(false);
      }
    }

    React.useEffect(() => {
      Role.list();
    }, [roleName]);

    React.useEffect(() => {
      if (detailsAction === "edit" && selectCollaborator?.email) {
        colEmailRef.current = selectCollaborator.email;
      }
      if (detailsAction === "create") {
        colEmailRef.current = "";
      }
    }, [detailsAction]);

    if (detailsAction === "view") {
      return (
        <>
          <div className="colDetails">
            <div>
              <Avatar sx={avatarSX} src={selectCollaborator?.avatar} />
            </div>
            <span>{selectCollaborator?.name}</span>
          </div>
          <br />
          <div className="colDetailsProperties">
            <div className="colDetailsProperty">
              <span className="propertyTitle">Email:</span>
              <span className="propertyValue">{selectCollaborator?.email}</span>
            </div>
          </div>
        </>
      );
    }

    if (detailsAction === "edit") {
      const rls = selectCollaborator?.roles || [];
      return (
        <div id="colDetailEdit">
          <div className="colDetails">
            <div>
              <Avatar sx={avatarSX} src={selectCollaborator?.avatar} />
            </div>
            <span>{selectCollaborator?.name}</span>
          </div>
          <br />
          <div className="colDetailsProperties">
            <TextField
              id="colDetailsEmail"
              placeholder="Email"
              icon={<FontAwesomeIcon icon={faEnvelope} />}
              defaultValue={selectCollaborator?.email}
              onInput={(e) => {
                colEmailRef.current = (e.target as HTMLInputElement).value;
              }}
              size="fill"
            />
            {(checkPermission(permissions.orgRoles, "grant") ||
              checkPermission(permissions.orgRoles, "revoke")) && (
              <div className="colRoleConfig">
                <span>Cargos:</span>
                {checkPermission(permissions.orgRoles, "grant") && (
                  <div className="colDetailsAddRole" onClick={() => setDetailsAction("grant")}>
                    <FontAwesomeIcon icon={faPlus} />
                  </div>
                )}
                {rls.map((role) => (
                  <Chip k={String(Math.random())} class="colDetailsRoleAlready">
                    {role.name}
                    {checkPermission(permissions.orgRoles, "revoke") && (
                      <Close fontSize="small" onClick={() => Role.revoke(role.id)} />
                    )}
                  </Chip>
                ))}
              </div>
            )}
          </div>
          <br />
          <Button
            type="success"
            icon={<FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: "20px" }} />}
            iconSlot="end"
            callback={() => editCollaborator()}
            disabled={!checkPermission(permissions.orgCollaborators, "edit")}
            expand
          >
            <span style={{ fontSize: "20px" }}>Editar</span>
          </Button>
        </div>
      );
    }

    if (detailsAction === "grant") {
      const rls = allRoles || [];
      return (
        <div id="colDetailEdit">
          <div className="colDetailsProperties">
            <TextField
              id="orgColPossibleRoleSearchField"
              placeholder="Pesquisar"
              icon={<FontAwesomeIcon icon={faSearch} />}
              size="fill"
              onInput={(e) => setRoleName((e.target as HTMLInputElement).value)}
              clear
            />
          </div>
          <div className="colRoleConfig">
            {rls.map((role) => (
              <Chip
                k={String(Math.random())}
                type="button"
                class={possibleRoles.includes(role.id) ? "colDetailsRoleActive" : "colDetailsRole"}
                callback={() => {
                  if (!possibleRoles.includes(role.id)) {
                    setPossibleRoles([...possibleRoles, role.id]);
                    return;
                  }
                  setPossibleRoles(possibleRoles.filter((id) => id !== role.id));
                }}
              >
                {possibleRoles.includes(role.id) && <Check fontSize="small" />}
                {role.name}
              </Chip>
            ))}
          </div>
          <br />
          <div className="colActionRoleBtns">
            <Button
              type="danger"
              icon={<FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "20px" }} />}
              iconSlot="start"
              callback={() => setDetailsAction("edit")}
              expand
            >
              <span style={{ fontSize: "20px" }}>Voltar</span>
            </Button>
            <Button
              type="success"
              icon={<FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: "20px" }} />}
              iconSlot="end"
              callback={() => Role.grant()}
              disabled={!checkPermission(permissions.orgRoles, "grant")}
              expand
            >
              <span style={{ fontSize: "20px" }}>Adicionar</span>
            </Button>
          </div>
        </div>
      );
    }

    if (detailsAction === "create") {
      return (
        <div id="colDetailCreate">
          <h1>Novo Colaborador</h1>
          <br />
          <div className="colDetailsProperties">
            <TextField
              id="colDetailsEmail"
              placeholder="Email"
              icon={<FontAwesomeIcon icon={faEnvelope} />}
              onInput={(e) => {
                colEmailRef.current = (e.target as HTMLInputElement).value;
                onInputEmail(e);
              }}
              size="fill"
            />
          </div>
          <br />
          <Button
            type="success"
            icon={<FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: "20px" }} />}
            iconSlot="end"
            callback={backend.inviteCollaborator}
            disabled={
              !createInviteCollaboratorHasValidEmail ||
              !checkPermission(permissions.orgCollaborators, "create")
            }
            expand
          >
            <span style={{ fontSize: "20px" }}>Adicionar</span>
          </Button>
        </div>
      );
    }

    return <></>;
  }

  React.useEffect(() => {
    backend.listOwners();
    backend.listPendingInvites();
    sessionInfo().then((session) => setCurrentCollaboratorID(session?.iss ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    backend.resetListCollaborators();
    backend.listPendingInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, canViewCollaborators]);

  return (
    <div id="orgColPage">
      {detailsOpen && (
        <FloatContainer
          close={() => {
            setDetailsOpen(false);
            setSelectCollaborator(null);
          }}
        >
          <BuildCollaboratorDetailContent />
        </FloatContainer>
      )}
      <Title title="Administradores" />
      <div id="orgColOwnerContent">
        {owners.map((owner) => (
          <div key={owner.id} className="orgColOwnerItem">
            <div>
              <Avatar sx={avatarSX} src={owner.avatar} />
            </div>
            <span>{owner.name}</span>
          </div>
        ))}
      </div>
      <br />
      {pendingInvites.length > 0 && (
        <>
          <Title title="Convites pendentes" />
          <div className="orgPendingInvites">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="orgPendingInviteItem">
                <div>
                  <strong>{invite.email}</strong>
                  <span>Expira em {new Date(invite.expiresAt).toLocaleDateString("pt-BR")}</span>
                </div>
                {checkPermission(permissions.orgCollaborators, "delete") && (
                  <button type="button" onClick={() => backend.revokeInvite(invite.id)}>
                    Revogar
                  </button>
                )}
              </div>
            ))}
          </div>
          <br />
        </>
      )}
      <Title title="Colaboradores" />
      <div id="orgGeneralCol">
        <BannerFeedback
          type={bannerFeedbackType}
          open={bannerFeedbackIsOpen}
          message={bannerFeedbackMessage}
          callback={() => setBannerFeedbackIsOpen(false)}
          sx={{
            marginTop: "var(--spacing-2xl)",
            maxWidth: "var(--width-xl)",
          }}
        />
        <div id="orgColActions">
          <TextField
            id="orgColSearchField"
            placeholder="Pesquisar"
            icon={<FontAwesomeIcon icon={faSearch} />}
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            clear
          />
          <Button
            callback={() => {
              setDetailsAction("create");
              setDetailsOpen(true);
            }}
            disabled={!checkPermission(permissions.orgCollaborators, "create")}
            icon={<FontAwesomeIcon icon={faAdd} />}
            iconSlot="start"
          >
            Adicionar Colaborador
          </Button>
        </div>
        <div id="orgColList">
          {collaborators.map((collaborator) => (
            <Chip
              id={collaborator.id}
              k={collaborator.id}
              type="button"
              class="orgColItem"
              callback={handleMenu.open}
            >
              <div className="orgColItemInner">
                <div>
                  <Avatar sx={avatarSX} src={collaborator.avatar} />
                </div>
                <span>{collaborator.name}</span>
              </div>
            </Chip>
          ))}
        </div>
        <Menu
          id="colRoleMenu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenu.close}
          sx={menuSX}
        >
          <MenuItem
            className="colRoleMenu"
            onClick={() => {
              setDetailsAction("view");
              setDetailsOpen(true);
              handleMenu.close();
            }}
          >
            <FontAwesomeIcon icon={faEye} fixedWidth />
            <span>Visualizar</span>
          </MenuItem>
          {checkPermission(permissions.orgCollaborators, "edit") &&
            selectCollaborator?.isOwner !== undefined &&
            !selectCollaborator?.isOwner && (
              <MenuItem
                className="colRoleMenu"
                onClick={() => {
                  setDetailsAction("edit");
                  setDetailsOpen(true);
                  handleMenu.close();
                }}
              >
                <FontAwesomeIcon icon={faEdit} fixedWidth />
                <span>Editar</span>
              </MenuItem>
            )}
          {checkPermission(permissions.orgCollaborators, "delete") &&
            selectCollaborator?.isOwner !== undefined &&
            !selectCollaborator?.isOwner && (
              <MenuItem
                className="colRoleMenu"
                onClick={() => backend.deleteCollaborator(selectCollaborator?.id)}
              >
                <FontAwesomeIcon icon={faClose} fixedWidth />
                <span>Excluir</span>
              </MenuItem>
            )}
          {owners.some((owner) => owner.id === currentCollaboratorID) &&
            selectCollaborator?.id !== currentCollaboratorID &&
            !selectCollaborator?.isOwner && (
              <MenuItem
                className="colRoleMenu"
                onClick={() => backend.transferOwnership(selectCollaborator)}
              >
                <FontAwesomeIcon icon={faKey} fixedWidth />
                <span>Transferir propriedade</span>
              </MenuItem>
            )}
        </Menu>
      </div>
    </div>
  );
}

/**
 * Weekly Pix Automatico authorization. The panel mirrors the permissions
 * Payments enforces on each route (org_payments create/edit/delete); the route
 * loader already requires view. The processing fee it shows comes from
 * Payments' readiness answer, and without a reviewed one no start is offered.
 * The page owns the polling hook, because the week card reads the same limit.
 */
function PixAutomaticoPayments({
  pix,
  termsVersion,
}: {
  pix: ReturnType<typeof usePixAutomatico>;
  termsVersion: string;
}) {
  const { permissions } = usePermissions();
  // Only a version /termos publishes may be accepted (termosEnterprise.ts).
  const termsPublished = isPixAutomaticoTermsPublished(termsVersion);
  return (
    <PixAutomaticoPanel
      view={pix.view}
      cancellationEligibility={pix.cancellationEligibility}
      weeklyLimit={pix.weeklyLimit}
      activationEnabled={pix.activationEnabled}
      weeklyAdmissionActive={pix.weeklyAdmissionActive}
      canCreate={checkPermission(permissions.orgPayments, "create")}
      canEdit={checkPermission(permissions.orgPayments, "edit")}
      canDelete={checkPermission(permissions.orgPayments, "delete")}
      termsVersion={termsVersion}
      termsPublished={termsPublished}
      processingFee={pix.processingFee}
      minimumChargeCents={pix.minimumChargeCents}
      limitBelowMinimum={pix.limitBelowMinimum}
      notice={pix.notice}
      pollingIssue={pix.pollingIssue}
      starting={pix.starting}
      cancelling={pix.cancelling}
      savingLimit={pix.savingLimit}
      onStart={(weeklyLimit) => {
        if (termsPublished) void pix.start(weeklyLimit);
      }}
      onCancel={() => void pix.cancel()}
      onSaveWeeklyLimit={pix.saveWeeklyLimit}
      onRetry={() => void pix.retry()}
      onDismissNotice={pix.dismissNotice}
    />
  );
}

/**
 * Saldo de Negociações (the prepaid balance Hall bids reserve), visually
 * apart from Pix Automático. It renders nothing unless Payments answers
 * enabled with an account for this company, and its actions mirror the
 * org_payments permissions Payments enforces on each route; the route loader
 * already requires view. Its ledger lives in the page's history tabs.
 */
function NegotiationBalancePayments({
  onHistoryKeyChange,
}: {
  onHistoryKeyChange: (reloadKey: number | null) => void;
}) {
  const { permissions } = usePermissions();
  return (
    <NegotiationBalancePanel
      canView={checkPermission(permissions.orgPayments, "view")}
      canCreate={checkPermission(permissions.orgPayments, "create")}
      canEdit={checkPermission(permissions.orgPayments, "edit")}
      onHistoryKeyChange={onHistoryKeyChange}
    />
  );
}

/** The week still being charged: an open weekly parent, never a child or a contribution. */
function isCurrentWeek(tx: Transaction): boolean {
  return (
    tx.weeklySummary !== undefined &&
    tx.invoiceStatus === "weekly-open" &&
    !tx.weeklyParentInvoiceId &&
    tx.weeklyBilling?.disposition !== "contribution"
  );
}

/** Rows grouped by the month they were registered in, newest first as listed. */
function groupByMonth(
  items: Transaction[],
): { key: string; label: string; items: Transaction[] }[] {
  const groups: { key: string; label: string; items: Transaction[] }[] = [];
  for (const tx of items) {
    const date = new Date(tx.createdAt);
    const key = Number.isFinite(date.getTime())
      ? `${date.getFullYear()}-${date.getMonth()}`
      : "unknown";
    let group = groups.find((candidate) => candidate.key === key);
    if (!group) {
      const label =
        key === "unknown"
          ? "Sem data"
          : date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
      group = { key, label: label.charAt(0).toUpperCase() + label.slice(1), items: [] };
      groups.push(group);
    }
    group.items.push(tx);
  }
  return groups;
}

export function Payments() {
  const [transactions, setTransactions] = React.useState<ListTransactions>({
    items: [],
    totalPages: 0,
  });
  const [currentPage, setCurrentPage] = React.useState(0);
  const latestTransactionsRequest = React.useRef(0);
  const [selectedBalanceId, setSelectedBalanceId] = React.useState<string>("");
  // Read-only pre-cutover invoice details. Weekly Pix rows use their statement
  // lines and never open this legacy provider settlement view.
  const [selectedSplitTx, setSelectedSplitTx] = React.useState<Transaction | null>(null);
  const [selectedWeekly, setSelectedWeekly] = React.useState<WeeklySelection | null>(null);
  const selectedWeeklyInvoice = React.useRef<string | null>(null);
  const latestWeeklyRequest = React.useRef(0);
  const [weeklyDetail, setWeeklyDetail] = React.useState<WeeklyStatementPage>();
  const [weeklyDetailIndex, setWeeklyDetailIndex] = React.useState(0);
  const [loadingWeeklyDetail, setLoadingWeeklyDetail] = React.useState(false);
  const [weeklyDetailError, setWeeklyDetailError] = React.useState(false);
  const [loadingTransactions, setLoadingTransactions] = React.useState<boolean>(false);
  const [errorInListTransaction, setErrorListTransactions] = React.useState<boolean>(false);
  const [banner, setBanner] = React.useState({
    open: false,
    msg: "",
    type: "success",
  });

  // The open week, read from the first page only: later pages are older weeks.
  const [currentWeek, setCurrentWeek] = React.useState<Transaction | null>(null);
  const [historyTab, setHistoryTab] = React.useState<"invoices" | "balance">("invoices");
  // The balance revision the ledger reloads on; null while there is no account.
  const [balanceHistoryKey, setBalanceHistoryKey] = React.useState<number | null>(null);
  const termsVersion = pixAutomaticoTermsVersion();
  const pix = usePixAutomatico(termsVersion);

  class api {
    static async fetchTransactions(page = 0) {
      const requestId = ++latestTransactionsRequest.current;
      setCurrentPage(page);
      setErrorListTransactions(false);
      setLoadingTransactions(true);
      try {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (requestId !== latestTransactionsRequest.current) return;
        if (!JWT) throw new Error("missing payment history session");

        const headers: { [key: string]: string | undefined } = { "X-JWT": JWT };
        const params: { [key: string]: string | undefined } = {
          page: String(page),
          size: "5",
          statement: "summary",
        };
        const res = await paymentAPI.get("/transactions", { headers, params });
        if (requestId !== latestTransactionsRequest.current) return;
        const totalPages = res.data.totalPages;
        if (!Number.isSafeInteger(totalPages) || totalPages < 0) {
          throw new Error("invalid transactions page count");
        }
        if (totalPages > 0 && page >= totalPages) {
          await api.fetchTransactions(totalPages - 1);
          return;
        }
        const items = res.data.items || [];
        if (!Array.isArray(items)) throw new Error("invalid transactions response");
        const parsedItems = items.map(
          (item: Transaction & { statementLines?: unknown; statementSummary?: unknown }) => {
            const summary = parseWeeklyStatementSummary(item.statementSummary);
            if (summary === null) throw new Error("invalid weekly summary response");
            const legacy = summary
              ? undefined
              : parseWeeklyStatement(
                  item.statementLines,
                  item.weeklyBilling?.policyVersion === 2 ? 2 : 1,
                );
            if (legacy === null) throw new Error("invalid weekly statement response");
            const weeklySummary =
              summary ??
              (legacy
                ? {
                    serviceAmountCents: legacy.serviceAmountCents,
                    customerCreditAmountCents: legacy.customerCreditAmountCents,
                    processingFeeAmountCents: legacy.processingFeeAmountCents,
                    ...(legacy.negotiationTopupAmountCents !== undefined
                      ? { negotiationTopupAmountCents: legacy.negotiationTopupAmountCents }
                      : {}),
                    totalAmountCents: legacy.totalAmountCents,
                    groupCount: legacy.lines.length,
                  }
                : undefined);
            if (item.amountKnown !== undefined && typeof item.amountKnown !== "boolean")
              throw new Error("invalid amount authority response");
            if (item.paymentOrigin !== undefined && item.paymentOrigin !== "weekly_pix")
              throw new Error("invalid payment origin response");
            if (item.paymentOrigin === "weekly_pix" && item.amountKnown !== true)
              throw new Error("invalid weekly child authority response");
            if (
              item.amountKnown === true &&
              (!Number.isSafeInteger(item.amount) || item.amount < 0)
            )
              throw new Error("invalid known weekly amount response");
            if (
              item.weeklyParentInvoiceId !== undefined &&
              (item.paymentOrigin !== "weekly_pix" ||
                typeof item.weeklyParentInvoiceId !== "string" ||
                !item.weeklyParentInvoiceId.trim() ||
                item.weeklyParentInvoiceId.length > 200)
            )
              throw new Error("invalid weekly parent binding response");
            const weeklyPeriod = parseWeeklyPeriod(item.weeklyPeriod);
            const weeklyCollection = parseWeeklyCollection(item.weeklyCollection);
            const weeklyBilling = parseWeeklyBilling(item.weeklyBilling, weeklySummary);
            if (weeklyPeriod === null || weeklyCollection === null || weeklyBilling === null)
              throw new Error("invalid weekly projection response");
            return { ...item, weeklySummary, weeklyPeriod, weeklyCollection, weeklyBilling };
          },
        );
        setTransactions({ totalPages, items: parsedItems });
        setCurrentPage(totalPages === 0 ? 0 : page);
        if (page === 0) setCurrentWeek(parsedItems.find(isCurrentWeek) ?? null);
      } catch {
        if (requestId === latestTransactionsRequest.current) {
          console.error("Failed to load payment history");
          setErrorListTransactions(true);
        }
      } finally {
        if (requestId === latestTransactionsRequest.current) {
          setLoadingTransactions(false);
        }
      }
    }

    static async fetchWeekly(invoiceId: string, page = 0) {
      const requestId = ++latestWeeklyRequest.current;
      const current = () =>
        requestId === latestWeeklyRequest.current && selectedWeeklyInvoice.current === invoiceId;
      setLoadingWeeklyDetail(true);
      setWeeklyDetailError(false);
      try {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (!current()) return;
        if (!JWT) throw new Error("missing weekly history session");
        const res = await paymentAPI.get(
          `/transactions/${encodeURIComponent(invoiceId)}/statement`,
          {
            headers: { "X-JWT": JWT },
            params: { page: String(page), size: "25" },
          },
        );
        if (!current()) return;
        const parsed = parseWeeklyStatementPage(res.data);
        if (!parsed || parsed.invoiceId !== invoiceId)
          throw new Error("invalid weekly statement page");
        if (page > 0 && page >= parsed.totalPages) {
          await api.fetchWeekly(invoiceId, 0);
          return;
        }
        setWeeklyDetail(parsed);
        setWeeklyDetailIndex(page);
        const snapshot = {
          invoiceId,
          summary: parsed.summary,
          period: parsed.weeklyPeriod,
          invoiceStatus: parsed.invoiceStatus,
          collection: parsed.weeklyCollection,
          billing: parsed.weeklyBilling,
        };
        setSelectedWeekly((selected) => (selected?.invoiceId === invoiceId ? snapshot : selected));
        setCurrentWeek((week) =>
          week?.invoiceId === invoiceId
            ? {
                ...week,
                weeklySummary: parsed.summary,
                weeklyPeriod: parsed.weeklyPeriod,
                invoiceStatus: parsed.invoiceStatus,
                weeklyCollection: parsed.weeklyCollection,
                weeklyBilling: parsed.weeklyBilling,
              }
            : week,
        );
        setTransactions((previous) => ({
          ...previous,
          items: previous.items.map((item) =>
            item.invoiceId === invoiceId
              ? {
                  ...item,
                  weeklySummary: parsed.summary,
                  weeklyPeriod: parsed.weeklyPeriod,
                  invoiceStatus: parsed.invoiceStatus,
                  weeklyCollection: parsed.weeklyCollection,
                  weeklyBilling: parsed.weeklyBilling,
                }
              : item,
          ),
        }));
        if (parsed.weeklyBilling?.disposition === "contribution")
          void api.fetchTransactions(currentPage);
      } catch {
        if (current()) setWeeklyDetailError(true);
      } finally {
        if (current()) setLoadingWeeklyDetail(false);
      }
    }
  }

  React.useEffect(() => {
    api.fetchTransactions();
    return () => {
      latestTransactionsRequest.current++;
      latestWeeklyRequest.current++;
      selectedWeeklyInvoice.current = null;
    };
  }, []);

  function openWeeklyStatement(tx: Transaction) {
    const invoiceId = tx.weeklyParentInvoiceId ?? tx.invoiceId;
    const parent =
      invoiceId === tx.invoiceId
        ? tx
        : transactions.items.find((item) => item.invoiceId === invoiceId && item.weeklySummary);
    selectedWeeklyInvoice.current = invoiceId;
    setSelectedWeekly({
      invoiceId,
      summary: parent?.weeklySummary,
      period: parent?.weeklyPeriod,
      invoiceStatus: parent?.invoiceStatus,
      collection: parent?.weeklyCollection,
      billing: parent?.weeklyBilling,
    });
    setWeeklyDetail(undefined);
    setWeeklyDetailIndex(0);
    void api.fetchWeekly(invoiceId);
  }

  function closeWeeklyStatement() {
    selectedWeeklyInvoice.current = null;
    latestWeeklyRequest.current++;
    setSelectedWeekly(null);
    setWeeklyDetail(undefined);
    setLoadingWeeklyDetail(false);
    setWeeklyDetailError(false);
  }

  function isWeeklyCandidate(tx: Transaction) {
    return tx.paymentOrigin === "weekly_pix" || tx.amountKnown === false;
  }

  function openTransaction(tx: Transaction) {
    if (tx.weeklySummary || tx.weeklyParentInvoiceId) openWeeklyStatement(tx);
    else if (isWeeklyCandidate(tx))
      setBanner({
        open: true,
        type: "error",
        msg: "A composição semanal desta negociação ainda não está confirmada.",
      });
    else setSelectedSplitTx(tx);
  }

  // Clicking the NFE icon of an unreconciled row used to be a pure no-op: it set
  // an empty balance id, and NFEsModal early-returns on that — no request, no
  // float, no log. The button stays ENABLED (a disabled one fires no click, is
  // dropped from the tab order and announces nothing, which is strictly worse)
  // and now explains itself.
  function openNFEs(tx: Transaction) {
    const intent = resolveNFEIntent(tx);
    if (intent.kind === "unavailable") {
      setBanner({ open: true, type: "error", msg: intent.message });
      return;
    }
    setSelectedBalanceId(intent.balanceId);
  }

  const chipTransactionCard = (tx: Transaction) => {
    const identified = hasIdentifiedCard(tx.cardLabel);
    return (
      <Chip class={`transactionCardChip${identified ? "" : " transactionCardChipUnidentified"}`}>
        {getBrandIcon(tx.cardBrand)}
        <span>{displayCardLabel(tx.cardLabel)}</span>
      </Chip>
    );
  };

  const chipTransactionPayment = (tx: Transaction) => {
    if (!tx.weeklySummary && !isWeeklyCandidate(tx)) return chipTransactionCard(tx);
    return (
      <Chip class="transactionCardChip transactionCardChipWeekly">
        <span>{tx.amountKnown === false ? "Composição pendente" : "Pix Automático"}</span>
      </Chip>
    );
  };

  const transactionTitle = (tx: Transaction) => {
    if (tx.weeklySummary) return "Cobrança semanal";
    if (isWeeklyCandidate(tx)) return "Negociação semanal";
    return tx.invoiceType === "standard" ? "Fatura mensal" : "Negociações";
  };

  const formatRegisteredAt = (createdAt: string) =>
    new Date(createdAt).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const isMobile = useMediaQuery("(max-width: 767px)");
  // The ledger tab exists only while the company has a Saldo de Negociações.
  const hasBalanceTab = balanceHistoryKey !== null;
  const activeHistoryTab = hasBalanceTab ? historyTab : "invoices";

  const transactionAmount = (tx: Transaction, interactive: boolean) => {
    if (tx.weeklySummary) {
      const summary = (
        <WeeklyPixSummary
          statement={tx.weeklySummary}
          period={tx.weeklyPeriod}
          invoiceStatus={tx.invoiceStatus}
          billing={tx.weeklyBilling}
        />
      );
      return interactive ? (
        <button
          type="button"
          className="walletTransactionAmountTrigger weeklyStatementTrigger"
          data-testid="wallet-transaction-amount"
          data-testid-alias="invoice-total"
          aria-label={`Abrir extrato semanal${tx.weeklyPeriod ? ` de ${displayWeeklyPeriod(tx.weeklyPeriod)}` : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            openWeeklyStatement(tx);
          }}
        >
          {summary}
        </button>
      ) : (
        <span data-testid="wallet-transaction-amount" data-testid-alias="invoice-total">
          {summary}
        </span>
      );
    }

    const amount = tx.amountKnown === false ? "Valor indisponível" : formatBRL(tx.amount);
    if (!interactive) {
      return (
        <span data-testid="wallet-transaction-amount" data-testid-alias="invoice-total">
          {amount}
        </span>
      );
    }
    return (
      <button
        type="button"
        className="walletTransactionAmountTrigger"
        data-testid="wallet-transaction-amount"
        data-testid-alias="invoice-total"
        data-tx-id={tx.invoiceId}
        onClick={(event) => {
          event.stopPropagation();
          openTransaction(tx);
        }}
      >
        {amount}
      </button>
    );
  };

  const nfeButton = (tx: Transaction, onBeforeClick?: (e: React.MouseEvent) => void) => {
    const intent = resolveNFEIntent(tx);
    const label = intent.kind === "open" ? NFE_AVAILABLE_ACTION_LABEL : intent.message;
    return (
      <button
        type="button"
        className={`btnTableNFE${intent.kind === "open" ? "" : " btnTableNFEUnavailable"}`}
        aria-label={label}
        title={label}
        data-nfe-available={intent.kind === "open"}
        onClick={(e) => {
          onBeforeClick?.(e);
          openNFEs(tx);
        }}
      >
        <FontAwesomeIcon className="btnTableNFEIcon" icon={faFileInvoiceDollar} />
      </button>
    );
  };

  const chipTransactionStatus = (tx: Transaction) => (
    <Chip class={`transactionStatusChip ${transactionStatusChipClass(tx)}`}>
      {transactionStatusLabel(tx)}
    </Chip>
  );

  function transactionStatusLabel(tx: Transaction) {
    if (!tx.weeklySummary && isWeeklyCandidate(tx))
      return tx.amountKnown === false ? "Composição não confirmada" : "Autorizada para a semana";
    return displayTransactionStatus(tx);
  }

  return (
    <>
      <NFEsModal balanceId={selectedBalanceId} onClose={() => setSelectedBalanceId("")} />

      {selectedWeekly && (
        <WeeklyStatementDialog
          open
          summary={selectedWeekly.summary}
          period={selectedWeekly.period}
          invoiceStatus={selectedWeekly.invoiceStatus ?? ""}
          collection={selectedWeekly.collection}
          billing={selectedWeekly.billing}
          onOpenTarget={(invoiceId) => {
            selectedWeeklyInvoice.current = invoiceId;
            setSelectedWeekly({ invoiceId });
            setWeeklyDetail(undefined);
            setWeeklyDetailIndex(0);
            void api.fetchWeekly(invoiceId);
          }}
          page={weeklyDetail}
          pageIndex={weeklyDetailIndex}
          refreshing={loadingWeeklyDetail}
          refreshError={weeklyDetailError}
          onClose={closeWeeklyStatement}
          onRefresh={() => void api.fetchWeekly(selectedWeekly.invoiceId, weeklyDetailIndex)}
          onPageChange={(page) => void api.fetchWeekly(selectedWeekly.invoiceId, page)}
        />
      )}

      <InvoiceSplitPreviewModal
        open={selectedSplitTx !== null}
        invoiceId={selectedSplitTx?.invoiceId}
        settlement={
          selectedSplitTx?.balanceId &&
          typeof selectedSplitTx.netAmountCents === "number" &&
          typeof selectedSplitTx.enterpriseResidualCents === "number" &&
          typeof selectedSplitTx.rewardsAmountCents === "number" &&
          typeof selectedSplitTx.contabileAmountCents === "number"
            ? {
                grossAmountCents: selectedSplitTx.grossAmountCents ?? selectedSplitTx.amount,
                netAmountCents: selectedSplitTx.netAmountCents,
                enterpriseResidualCents: selectedSplitTx.enterpriseResidualCents,
                rewardsAmountCents: selectedSplitTx.rewardsAmountCents,
                contabileAmountCents: selectedSplitTx.contabileAmountCents,
                enterpriseCashReceivedCents: selectedSplitTx.enterpriseCashReceivedCents ?? 0,
                settlementStatus: selectedSplitTx.settlementStatus ?? "pending",
                enterpriseSettlementStatus: selectedSplitTx.enterpriseSettlementStatus ?? "pending",
              }
            : undefined
        }
        onClose={() => setSelectedSplitTx(null)}
      />

      <Title
        title="Pagamentos"
        subtitle="A cobrança semanal da DaKasa, o saldo dos lances e tudo o que já foi pago"
      />

      <BannerFeedback
        open={banner.open}
        message={banner.msg}
        type={banner.type as any}
        callback={() => setBanner({ ...banner, open: false })}
        sx={{ marginTop: "var(--spacing-2xl)" }}
      />

      <div className="orgPayments">
        {currentWeek?.weeklySummary && (
          <WeeklyChargeCard
            summary={currentWeek.weeklySummary}
            period={currentWeek.weeklyPeriod}
            invoiceStatus={currentWeek.invoiceStatus}
            billing={currentWeek.weeklyBilling}
            collection={currentWeek.weeklyCollection}
            weeklyLimitCents={pix.weeklyLimit?.mode === "fixed" ? pix.weeklyLimit.cents : undefined}
            processingFeeCents={
              pix.processingFee?.state === "reviewed"
                ? pix.processingFee.centsPerWeeklyCharge
                : undefined
            }
            onOpenStatement={() => openWeeklyStatement(currentWeek)}
          />
        )}

        <div className="orgPaymentsAccounts">
          <PixAutomaticoPayments pix={pix} termsVersion={termsVersion} />
          <NegotiationBalancePayments onHistoryKeyChange={setBalanceHistoryKey} />
        </div>

        <section className="orgPaymentsHistory" aria-labelledby="orgPaymentsHistoryTitle">
          <header className="historyHeader">
            <h2 className="historyTitle" id="orgPaymentsHistoryTitle">
              Histórico
            </h2>
            {hasBalanceTab && (
              <div className="historyTabs" role="tablist" aria-label="Histórico">
                <button
                  type="button"
                  role="tab"
                  id="orgPaymentsInvoicesTab"
                  aria-controls="orgPaymentsInvoicesPanel"
                  aria-selected={activeHistoryTab === "invoices"}
                  onClick={() => setHistoryTab("invoices")}
                >
                  Faturas
                </button>
                <button
                  type="button"
                  role="tab"
                  id="orgPaymentsBalanceTab"
                  aria-controls="orgPaymentsBalancePanel"
                  aria-selected={activeHistoryTab === "balance"}
                  onClick={() => setHistoryTab("balance")}
                >
                  Movimentações do saldo
                </button>
              </div>
            )}
          </header>

          {activeHistoryTab === "balance" && balanceHistoryKey !== null ? (
            <div
              className="historyBalance"
              role="tabpanel"
              id="orgPaymentsBalancePanel"
              aria-labelledby="orgPaymentsBalanceTab"
            >
              <BalanceHistory reloadKey={balanceHistoryKey} />
            </div>
          ) : (
            <div
              className="historyInvoices"
              data-testid="invoice-modal"
              role={hasBalanceTab ? "tabpanel" : undefined}
              id="orgPaymentsInvoicesPanel"
              aria-labelledby={hasBalanceTab ? "orgPaymentsInvoicesTab" : undefined}
            >
              {/*
                Cam 3 Task 1: data-testid hooks on the canonical invoice/transactions
                surface. Cam 3 Playwright (e2e/financial/) drives DOM via these IDs
                (selector policy: data-testid only, no class/text). See spec
                docs/superpowers/specs/2026-05-25-financial-cam3-playwright-design.md
                section 4. One list serves every viewport, so each row renders once.
              */}
              {loadingTransactions ? (
                <p className="historyEmpty" role="status">
                  Carregando pagamentos...
                </p>
              ) : errorInListTransaction ? (
                <div className="historyError">
                  <p className="historyEmpty" role="alert">
                    Ocorreu um erro ao carregar os dados. Por favor, tente novamente mais tarde.
                  </p>
                  <Button callback={() => void api.fetchTransactions(currentPage)}>
                    Tentar novamente
                  </Button>
                </div>
              ) : transactions.items.length === 0 ? (
                <p className="historyEmpty">
                  Nenhuma fatura ainda. A primeira aparece aqui quando a semana fechar.
                </p>
              ) : (
                groupByMonth(transactions.items).map((group) => (
                  <div className="historyGroup" key={group.key}>
                    <h3 className="historyMonth">{group.label}</h3>
                    <ul className="historyRows">
                      {group.items.map((tx) => (
                        <li
                          className="historyRow"
                          key={tx.invoiceId}
                          data-testid="wallet-transaction-row"
                          data-tx-id={tx.invoiceId}
                        >
                          <span className="rowIcon" aria-hidden="true">
                            {tx.weeklySummary || isWeeklyCandidate(tx) ? (
                              <EventRepeatOutlinedIcon fontSize="small" />
                            ) : (
                              <CreditCardOutlinedIcon fontSize="small" />
                            )}
                          </span>
                          <div className="rowMain">
                            <span className="rowTitle">{transactionTitle(tx)}</span>
                            <span className="rowMeta">
                              {chipTransactionPayment(tx)}
                              <span>Registrada em {formatRegisteredAt(tx.createdAt)}</span>
                            </span>
                          </div>
                          <div className="rowSide">
                            {chipTransactionStatus(tx)}
                            <div className="rowAmount">{transactionAmount(tx, true)}</div>
                            {nfeButton(tx)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
              {!loadingTransactions && !errorInListTransaction && transactions.totalPages > 1 && (
                <Pagination
                  className="historyPagination"
                  aria-label="Páginas de transações"
                  count={transactions.totalPages}
                  page={currentPage + 1}
                  siblingCount={isMobile ? 0 : 1}
                  size="small"
                  onChange={(_, nextPage) => {
                    if (nextPage - 1 !== currentPage) void api.fetchTransactions(nextPage - 1);
                  }}
                />
              )}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export function Organization() {
  const { permissions } = usePermissions();
  const [indexTopic, setIndexTopic] = React.useState<number>();
  const [updateFlag, setUpdateFlag] = React.useState(false);
  const topic = React.useRef<
    {
      link: string;
      title: string;
      icon: JSX.Element;
    }[]
  >([]);

  React.useEffect(() => {
    topic.current = [];
    const canViewOrgRoles = checkPermission(permissions.orgRoles, "view");
    const canViewOrgPayments = checkPermission(permissions.orgPayments, "view");
    const perm = [true, true, canViewOrgRoles, canViewOrgPayments];
    const topics = [
      {
        link: "/organizacao/info",
        icon: <CorporateFareTwoToneIcon />,
        title: "Visão Geral",
      },
      {
        link: "/organizacao/colaboradores",
        icon: <PeopleAltTwoToneIcon />,
        title: "Colaboradores",
      },
      {
        link: "/organizacao/iam",
        icon: <VpnKeyTwoToneIcon />,
        title: "IAM",
      },
      {
        link: "/organizacao/pagamentos",
        icon: <CreditCardTwoToneIcon />,
        title: "Pagamentos",
      },
    ];

    for (let i = 3; i >= 0; i--) {
      if (!perm[i]) continue;
      topic.current[i] = topics[i];
      if (topics[i].link !== window.location.pathname) continue;
      setIndexTopic(i);
    }
    setUpdateFlag(!updateFlag);
  }, [permissions]);

  if (indexTopic === undefined) return <></>;
  return (
    <div id="orgPage">
      <Topics
        active={indexTopic}
        callback={(index) => setIndexTopic(index)}
        topic={topic.current}
      />
      <Outlet />
    </div>
  );
}
